import { classifyError } from '@mononoke/shared';
import { ledger } from './browser.js';
import { MAX_OP_AGE_MS, type NewOp, type OpStore, type PendingOp } from './pending-ops.js';

/**
 * The replay loop — offline plan v1.1 §2.2 A and D, increment 2.
 *
 * WHAT THIS IS FOR. Capture a photo in a basement stockroom, close the tab, walk
 * upstairs, open the app: the photo uploads. The in-memory queue could not do that,
 * and said so in its own comment.
 *
 * WHAT IT DELIBERATELY DOES NOT DO. It does not queue INTAKE photos. Their destination
 * — the item — does not exist yet at capture time (intake.tsx creates the item and only
 * then attaches), so a persisted intake blob would survive a reload with nothing to
 * attach itself to. Making intake itself a queued op is increment 3, and this queue is
 * shaped to receive it: `kind` exists for exactly that reason.
 *
 * SENDING IS INJECTED. This module knows about ordering, retry, expiry and visibility;
 * it knows nothing about R2, tRPC or the shape of a photo. That is what lets the whole
 * loop be proven under vitest with no network — and it is the same seam `PutFn` already
 * uses in uploads.ts.
 */

export type SendFn = (op: PendingOp) => Promise<void>;

export interface OpView {
  id: string;
  /** What kind of act is waiting — the queue badge names the noun by it (0.1.507). */
  kind: PendingOp['kind'];
  status: PendingOp['status'];
  attempts: number;
  message?: string;
}

export interface OpQueue {
  enqueue(op: NewOp, id?: string): Promise<string>;
  /** Hydrate from disk, age out what is too old, then drain. Call once at boot. */
  start(): Promise<void>;
  /** Re-attempt one op the user asked about — including a non-retryable failure. */
  retry(id: string): void;
  /** Forget an op entirely. The only way an op leaves the queue unsent. */
  remove(id: string): Promise<void>;
  entries(): OpView[];
  /** The ops themselves, for the read cache's merge rule — `entries()` is the
   * render-shaped view and deliberately carries no payload. */
  ops(): PendingOp[];
  /** queued + sending. What a queue-depth indicator must show, and never round down. */
  pendingCount(): number;
  subscribe(listener: () => void): () => void;
  /** Detach the online listener. For tests and for a queue being torn down. */
  stop(): void;
}

export interface OpQueueDeps {
  store: OpStore;
  send: SendFn;
  /** Injected so expiry is testable without waiting thirty days. */
  now?: () => number;
  /** Injected so tests do not need a window. */
  onOnline?: (listener: () => void) => () => void;
}

const CONCURRENCY = 2;

/**
 * The HTTP status, read STRUCTURALLY rather than by importing TRPCClientError — this
 * module stays transport-agnostic, which is what lets the whole loop be tested with no
 * network.
 *
 * IT IS NOT OPTIONAL POLISH. Without it a tRPC failure reaches the classifier with no
 * status, matches nothing, and defaults to UNKNOWN/not-retryable — so a plain 500
 * (which the e2e stack produces on every `uploadUrls`, having no R2) parks the op in
 * `failed` and waits for a human forever. Caught by the offline e2e, which expected a
 * surviving queued op and found "1 photo needs attention".
 *
 * Two shapes, because two layers throw: `error.httpStatus` is what `fetchPut` attaches,
 * `error.data.httpStatus` is where tRPC puts it.
 */
function httpStatusOf(error: unknown): number | undefined {
  const candidate = error as { httpStatus?: unknown; data?: { httpStatus?: unknown } | null } | null;
  if (typeof candidate?.httpStatus === 'number') return candidate.httpStatus;
  const fromData = candidate?.data?.httpStatus;
  return typeof fromData === 'number' ? fromData : undefined;
}

const defaultOnOnline = (listener: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;
  window.addEventListener('online', listener);
  return () => {
    window.removeEventListener('online', listener);
  };
};

export function createOpQueue(deps: OpQueueDeps): OpQueue {
  const { store, send } = deps;
  const now = deps.now ?? (() => Date.now());
  const onOnline = deps.onOnline ?? defaultOnOnline;

  /** The in-memory mirror of the store. The store is the truth; this is what renders. */
  const ops = new Map<string, PendingOp>();
  const listeners = new Set<() => void>();
  let inFlight = 0;

  const notify = (): void => {
    for (const listener of listeners) listener();
  };

  /**
   * Persist THEN mirror, always in that order. A crash between the two leaves a
   * durable op the next boot will replay — which is the safe direction. Mirroring
   * first would show the user a queued photo that does not survive the reload.
   */
  const persist = async (op: PendingOp): Promise<void> => {
    await store.put(op);
    ops.set(op.id, op);
  };

  /**
   * Age out anything past the maintainer's thirty days. NOT deleted — moved to a
   * visible `expired` state and ledgered, because an app that silently discards a
   * photo somebody took is exactly what G10 forbids. The human dismisses it.
   */
  const expire = async (): Promise<boolean> => {
    const cutoff = now() - MAX_OP_AGE_MS;
    let changed = false;
    for (const op of [...ops.values()]) {
      if (op.status === 'expired' || op.createdAt > cutoff) continue;
      const aged: PendingOp = {
        ...op,
        status: 'expired',
        lastError: 'This has been waiting too long to send and needs your attention.',
      };
      ledger.record(new Error(`pending op expired after ${String(op.attempts)} attempt(s)`), 'queue:expired', {
        notified: true,
        context: { id: op.id, kind: op.kind, attempts: op.attempts },
      });
      await persist(aged);
      changed = true;
    }
    return changed;
  };

  /**
   * Start sends up to the concurrency limit and RETURN — do not await them.
   *
   * THE SHAPE MATTERS AND THE FIRST DRAFT GOT IT WRONG. An await-everything pass
   * blocks the whole loop on its slowest upload, so a photo added while another is
   * in flight sits idle instead of filling the free slot. Measured: three ops
   * enqueued, one sent. This is `uploads.ts`'s proven pump instead — completions
   * pump again, so the pipeline refills itself.
   *
   * THE `sending` MARK IS SYNCHRONOUS, and that is what stops an op being picked
   * twice: the mirror is what the search below reads, so it must be updated before
   * any await. On disk the op stays `queued` — durably correct either way, because
   * `start()` re-queues a `sending` op anyway.
   *
   * THERE IS NO RETRY TIMER, and that is a decision rather than an omission: a timer
   * retrying against a network that is still down spends battery to learn nothing. The
   * events that carry information drive the loop — coming back online, booting,
   * enqueuing, or a human pressing retry.
   */
  const pump = (): void => {
    while (inFlight < CONCURRENCY) {
      const next = [...ops.values()]
        .sort((a, b) => a.createdAt - b.createdAt)
        .find((op) => op.status === 'queued' && !attempted.has(op.id));
      if (!next) return;

      attempted.add(next.id);
      const attempt: PendingOp = { ...next, status: 'sending', attempts: next.attempts + 1 };
      ops.set(attempt.id, attempt);
      inFlight += 1;
      notify();

      void send(attempt)
        .then(async () => {
          // Gone from disk BEFORE gone from the mirror: an op removed from view but
          // still stored would replay on the next boot and act twice.
          await store.delete(attempt.id);
          ops.delete(attempt.id);
        })
        .catch(async (error: unknown) => {
          const httpStatus = httpStatusOf(error);
          const entry = ledger.record(error, 'queue:send', {
            notified: true,
            ...(httpStatus !== undefined ? { classify: { httpStatus } } : {}),
            context: { id: attempt.id, kind: attempt.kind, attempts: attempt.attempts },
          });
          // CLASSIFIED, never matched on the message (standing rule). A retryable
          // failure goes back to `queued` and waits for a real event; 400/403/409 is
          // the server saying no, and re-sending it would loop forever against a
          // refusal that is correct.
          //
          // 401 IS THE EXCEPTION, AND §2.3 NAMES IT: "a queued op replayed with a dead
          // token must re-auth, not drop". The classifier calls 401 non-retryable, which
          // is right for an interactive request — the user is told to sign in — and
          // WRONG here, where dropping the op would silently discard a capture because a
          // token expired while the phone was in a pocket. The op stays queued; signing
          // in again is what makes the next tick succeed.
          const retryable =
            httpStatus === 401 ||
            classifyError(error, httpStatus !== undefined ? { httpStatus } : {}).retryable;
          await persist({
            ...attempt,
            status: retryable ? 'queued' : 'failed',
            lastError: entry.userMessage,
          });
        })
        .finally(() => {
          inFlight -= 1;
          notify();
          // Refill the free slot with OTHER work. `attempted` is what stops this
          // re-picking the op that just failed: `.finally` runs AFTER the async
          // `.catch` has already put a retryable failure back to `queued`, so
          // filtering on status alone spins forever. That is not a hypothetical —
          // it exhausted the vitest worker on the first run of this suite.
          pump();
        });
    }
  };

  /**
   * Age out, then pump. Serialised so two ticks cannot expire concurrently, and
   * CAUGHT so one bad store write cannot poison the chain — without the catch, every
   * later tick chains onto a rejected promise and the queue goes permanently quiet
   * behind a UI still showing work pending.
   */
  let ticking: Promise<void> = Promise.resolve();
  /**
   * Ops already attempted in the current pass. ONE ATTEMPT PER OP PER TICK is the
   * whole retry policy: cleared when a new tick starts, because a new tick means a new
   * reason to believe — the network returned, the app booted, a human pressed retry,
   * or something else was enqueued.
   */
  let attempted = new Set<string>();
  const tick = (): Promise<void> => {
    ticking = ticking
      .then(async () => {
        attempted = new Set();
        await expire();
        pump();
      })
      .catch((error: unknown) => {
        ledger.record(error, 'queue:pass', { notified: true });
      });
    return ticking;
  };

  /**
   * ONE online listener for the queue, registered at construction — NOT per
   * subscriber. Tying it to `subscribe` would mean a queue nobody is rendering never
   * wakes up when the network returns, which is the exact moment it exists for.
   */
  const stopOnline = onOnline(() => {
    void tick();
  });

  return {
    async enqueue(newOp, id) {
      const op: PendingOp = {
        ...newOp,
        id: id ?? crypto.randomUUID(),
        // The capture instant. For a `movement` this is EVIDENCE the server records as
        // `occurred_at` — see pending-ops.ts.
        createdAt: now(),
        attempts: 0,
        status: 'queued',
      };
      await persist(op);
      notify();
      void tick();
      return op.id;
    },

    async start() {
      // An unreadable store is a REFUSAL, not an empty queue. Returning [] here would
      // tell the user there is nothing pending when there may be a stockroom's worth.
      let stored: PendingOp[];
      try {
        stored = await store.all();
      } catch (error) {
        ledger.record(error, 'queue:hydrate', { notified: true });
        throw error;
      }
      for (const op of stored) {
        // Anything caught mid-flight by a tab close is `sending` on disk and was never
        // acknowledged. It goes back to `queued`: the receipt on the server is what
        // makes re-sending safe (increment 1), which is why that had to land first.
        ops.set(op.id, op.status === 'sending' ? { ...op, status: 'queued' } : op);
      }
      notify();
      // Awaited so a caller knows hydration and ageing-out are DONE. It does not wait
      // for the uploads themselves — `start()` means "the queue is live and pumping",
      // not "everything has been sent", which on a stockroom's backlog could be
      // minutes and would block boot.
      await tick();
    },

    retry(id) {
      const op = ops.get(id);
      if (!op || op.status === 'sending') return;
      // RETRYING AN EXPIRED OP RESTARTS ITS CLOCK. Without this the next pass ages it
      // straight back out — `createdAt` is still thirty days ago — and the retry
      // button would be a button that visibly does nothing. The thirty days measures
      // how long the APP will keep trusting an op unattended; a human looking at it
      // and asking for it is the trust being renewed by the only party who can.
      const revived: PendingOp =
        op.status === 'expired'
          ? { ...op, status: 'queued', createdAt: now(), attempts: 0, lastError: undefined }
          : { ...op, status: 'queued', lastError: undefined };
      void persist(revived).then(() => {
        notify();
        void tick();
      });
    },

    /** Detach the online listener. For tests and for a queue being torn down. */
    stop() {
      stopOnline();
    },

    async remove(id) {
      await store.delete(id);
      ops.delete(id);
      notify();
    },

    ops() {
      return [...ops.values()].sort((a, b) => a.createdAt - b.createdAt);
    },

    entries() {
      return [...ops.values()]
        .sort((a, b) => a.createdAt - b.createdAt)
        .map(({ id, kind, status, attempts, lastError }) => ({
          id,
          kind,
          status,
          attempts,
          ...(lastError !== undefined ? { message: lastError } : {}),
        }));
    },

    pendingCount() {
      return [...ops.values()].filter((op) => op.status === 'queued' || op.status === 'sending').length;
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
